import dayjs from 'dayjs';

import { ISO_COUNTRY_CODES } from '../countries';
import {
  ConsentKey,
  CONSENT_FILTERS,
  ContactListState,
  countryName,
  DayRange,
  isBackwards,
  NO_COUNTRY,
  SOURCES,
  SUPPORT_CHOICES,
} from '../contactList';

/**
 * The filter bar's model: which filters there are, what each pill says, and
 * the date presets. Pure, so it is tested without rendering.
 */

export type FilterId =
  | 'tags'
  | 'lists'
  | 'country'
  | 'consent'
  | 'joined'
  | 'contacted'
  | 'support'
  | 'owner'
  | 'source'
  | 'stage';

export const FILTER_LABELS: Record<FilterId, string> = {
  tags: 'Tags',
  lists: 'Lists',
  country: 'Country',
  consent: 'Consent',
  joined: 'Joined',
  contacted: 'Last contacted',
  support: 'Support level',
  owner: 'Point person',
  source: 'Source',
  stage: 'Stage',
};

/** Always on the bar. */
export const PINNED_FILTERS: Array<FilterId> = ['tags', 'lists', 'country', 'consent', 'joined', 'contacted', 'support'];

/** Behind "More filters" until set; a set one gets its own pill. */
export const EXTRA_FILTERS: Array<FilterId> = ['owner', 'source', 'stage'];

const isOpen = (range: DayRange) => !range.from && !range.to;

export const isFilterActive = (id: FilterId, state: ContactListState): boolean => {
  switch (id) {
    case 'tags':
      return state.tagIds.length > 0 || state.excludeTagIds.length > 0;
    case 'lists':
      return state.listIds.length > 0;
    case 'country':
      return state.countries.length > 0 || state.noCountry;
    case 'consent':
      return Object.keys(state.consent).length > 0;
    case 'joined':
      return !isOpen(state.joined) || !isOpen(state.appJoined) || state.appMember !== undefined;
    case 'contacted':
      return state.neverContacted || !isOpen(state.contacted);
    case 'support':
      return Boolean(state.support);
    case 'owner':
      return state.noOwner || state.ownerIds.length > 0;
    case 'source':
      return state.sources.length > 0;
    case 'stage':
      return state.stages.length > 0;
    default:
      return false;
  }
};

/** What a pill's × sends. */
export const clearFilter = (id: FilterId): Partial<ContactListState> => {
  switch (id) {
    case 'tags':
      return { tagIds: [], excludeTagIds: [], tagMatch: 'any' };
    case 'lists':
      return { listIds: [] };
    case 'country':
      return { countries: [], noCountry: false, countryMatch: 'in' };
    case 'consent':
      return { consent: {} };
    case 'joined':
      return { joined: {}, appJoined: {}, appMember: undefined };
    case 'contacted':
      return { contacted: {}, neverContacted: false };
    case 'support':
      return { support: null };
    case 'owner':
      return { ownerIds: [], noOwner: false };
    case 'source':
      return { sources: [] };
    case 'stage':
      return { stages: [] };
    default:
      return {};
  }
};

const ALL_FILTERS: Array<FilterId> = [...PINNED_FILTERS, ...EXTRA_FILTERS];

/** How many filters are set, the search aside. */
export const activeFilterCount = (state: ContactListState) => ALL_FILTERS.filter((id) => isFilterActive(id, state)).length;

/** A date range has dates "to" before "from": not sent, and the pill says so. */
export const hasBackwardsDates = (id: FilterId, state: ContactListState) =>
  (id === 'joined' && (isBackwards(state.joined) || isBackwards(state.appJoined))) ||
  (id === 'contacted' && !state.neverContacted && isBackwards(state.contacted));

/* ------------------------------------------------------------------ *
 * Date presets
 * ------------------------------------------------------------------ */

export interface DatePreset {
  id: string;
  label: string;
  /** The range on `today` (YYYY-MM-DD). Open ends stay open, so "since" means up to now. */
  range: (today: string) => DayRange;
}

const shift = (today: string, days: number) => dayjs(today).add(days, 'day').format('YYYY-MM-DD');

const LAST_7: DatePreset = { id: '7d', label: 'Last 7 days', range: (today) => ({ from: shift(today, -6) }) };
const LAST_30: DatePreset = { id: '30d', label: 'Last 30 days', range: (today) => ({ from: shift(today, -29) }) };
const LAST_90: DatePreset = { id: '90d', label: 'Last 90 days', range: (today) => ({ from: shift(today, -89) }) };

/** For when people joined: recent spans and calendar years. */
export const JOINED_PRESETS: Array<DatePreset> = [
  { id: 'today', label: 'Today', range: (today) => ({ from: today }) },
  LAST_7,
  LAST_30,
  LAST_90,
  { id: 'year', label: 'This year', range: (today) => ({ from: `${today.slice(0, 4)}-01-01` }) },
  {
    id: 'lastYear',
    label: 'Last year',
    range: (today) => {
      const year = Number(today.slice(0, 4)) - 1;
      return { from: `${year}-01-01`, to: `${year}-12-31` };
    },
  },
];

/** For when people were last contacted: recent spans, and how long it has been. */
export const CONTACTED_PRESETS: Array<DatePreset> = [
  LAST_7,
  LAST_30,
  LAST_90,
  // The complement of "Last 90 days": up to and including the day before it starts.
  { id: 'over90', label: 'Over 90 days ago', range: (today) => ({ to: shift(today, -90) }) },
  {
    id: 'overYear',
    label: 'Over a year ago',
    range: (today) => ({ to: dayjs(today).subtract(1, 'year').format('YYYY-MM-DD') }),
  },
];

const sameRange = (a: DayRange, b: DayRange) => (a.from ?? '') === (b.from ?? '') && (a.to ?? '') === (b.to ?? '');

export const matchPreset = (range: DayRange, presets: Array<DatePreset>, today: string) =>
  isOpen(range) ? undefined : presets.find((preset) => sameRange(preset.range(today), range));

/** "Last 30 days", "1 Jan – 31 Mar 2026", "Since 1 Jan 2015", "Until 31 Dec 2015". */
export const describeDayRange = (range: DayRange, presets: Array<DatePreset>, today: string): string => {
  if (isOpen(range)) return '';
  if (isBackwards(range)) return 'Dates reversed';

  const preset = matchPreset(range, presets, today);
  if (preset) return preset.label;

  const long = (day: string) => dayjs(day).format('D MMM YYYY');

  if (range.from && range.to) {
    if (range.from === range.to) return `On ${long(range.from)}`;
    const short = range.from.slice(0, 4) === range.to.slice(0, 4) ? dayjs(range.from).format('D MMM') : long(range.from);
    return `${short} – ${long(range.to)}`;
  }

  return range.from ? `Since ${long(range.from)}` : `Until ${long(range.to as string)}`;
};

/* ------------------------------------------------------------------ *
 * What each pill says
 * ------------------------------------------------------------------ */

/** Names for ids, from the choices loaded so far; a fallback such as "Tag #12" otherwise. */
export interface FilterNames {
  tag: (id: number) => string;
  list: (id: number) => string;
  owner: (id: number) => string;
}

export const SOURCE_LABELS: Record<string, string> = {
  NATIONBUILDER: 'NationBuilder',
  APP: 'The app',
  IMPORT: 'Import (call sheet, SendGrid)',
  MANUAL: 'Added by hand',
  WORKFLOW: 'A workflow',
};

/** What a yes or a no on each consent filter means, for the pill. */
export const CONSENT_PHRASES: Record<ConsentKey, [string, string]> = {
  canEmail: ['Can email', "Can't email"],
  canCall: ['Can call', "Can't call"],
  canText: ['Can text', "Can't text"],
  doNotCall: ['Do not call', 'No do-not-call flag'],
  doNotContact: ['Do not contact', 'No do-not-contact flag'],
};

const plural = (n: number, one: string, many: string) => `${n.toLocaleString('en-US')} ${n === 1 ? one : many}`;

/** One name, or how many. */
const oneOrCount = (names: Array<string>, one: string, many: string) =>
  names.length === 1 ? names[0] : plural(names.length, one, many);

const describeCountries = (state: ContactListState) => {
  const every = state.countries.length >= ISO_COUNTRY_CODES.length && ISO_COUNTRY_CODES.every((code) => state.countries.includes(code));
  const names = [...state.countries.map(countryName), ...(state.noCountry ? ['No country'] : [])];
  const chosen = every ? (state.noCountry ? 'All countries and none' : 'All countries') : oneOrCount(names, 'country', 'countries');

  return state.countryMatch === 'out' ? `Not ${chosen}` : chosen;
};

const describeTags = (state: ContactListState, names: FilterNames) => {
  const parts: Array<string> = [];
  const wanted = state.tagIds;
  const unwanted = state.excludeTagIds;

  if (wanted.length === 1) parts.push(names.tag(wanted[0]));
  else if (wanted.length > 1) parts.push(`${state.tagMatch === 'all' ? 'All' : 'Any'} of ${wanted.length}`);

  if (unwanted.length === 1) parts.push(`not ${names.tag(unwanted[0])}`);
  else if (unwanted.length > 1) parts.push(`none of ${unwanted.length}`);

  return parts.join(', ');
};

const describeJoined = (state: ContactListState, today: string) => {
  const crm = describeDayRange(state.joined, JOINED_PRESETS, today);
  const appRange = describeDayRange(state.appJoined, JOINED_PRESETS, today);
  let app = '';
  if (state.appMember === false) app = 'No app account';
  else if (appRange) app = `App: ${appRange}`;
  else if (state.appMember) app = 'Has the app';

  return [crm, app].filter(Boolean).join(' · ');
};

/** The pill's value, or '' when the filter is not set. */
export const describeFilter = (id: FilterId, state: ContactListState, names: FilterNames, today: string): string => {
  if (!isFilterActive(id, state)) return '';

  switch (id) {
    case 'tags':
      return describeTags(state, names);
    case 'lists':
      return oneOrCount(state.listIds.map(names.list), 'list', 'lists');
    case 'country':
      return describeCountries(state);
    case 'consent': {
      const phrases = CONSENT_FILTERS.filter(({ key }) => state.consent[key] !== undefined).map(
        ({ key }) => CONSENT_PHRASES[key][state.consent[key] ? 0 : 1]
      );
      return phrases.length <= 2 ? phrases.join(', ') : `${phrases.length} set`;
    }
    case 'joined':
      return describeJoined(state, today);
    case 'contacted':
      return state.neverContacted ? 'Never' : describeDayRange(state.contacted, CONTACTED_PRESETS, today);
    case 'support':
      return SUPPORT_CHOICES.find((choice) => choice.value === state.support)?.label ?? '';
    case 'owner':
      return state.noOwner ? 'No point person' : oneOrCount(state.ownerIds.map(names.owner), 'person', 'people');
    case 'source':
      return oneOrCount(
        state.sources.map((source) => SOURCE_LABELS[source] ?? source),
        'source',
        'sources'
      );
    case 'stage':
      return oneOrCount(state.stages, 'stage', 'stages');
    default:
      return '';
  }
};

/** Every source, for the source filter. */
export const SOURCE_CHOICES = SOURCES.map((value) => ({ value, label: SOURCE_LABELS[value] ?? value }));

/**
 * Every country, not only those contacts have: the ones with contacts first,
 * largest first (with "No country" among them), then the rest A–Z. Codes in the
 * data but not in ISO 3166 are kept, so nothing loaded becomes unselectable.
 */
export const countryChoices = (counts: Map<string, number>): Array<string> => {
  const withContacts = Array.from(counts.entries())
    .filter(([, n]) => n > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([code]) => code);
  const listed = new Set(withContacts);
  const others = ISO_COUNTRY_CODES.filter((code) => !listed.has(code))
    .map((code) => ({ code, name: countryName(code) }))
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(({ code }) => code);

  return [...withContacts, ...(listed.has(NO_COUNTRY) ? [] : [NO_COUNTRY]), ...others];
};

export const countryLabel = (code: string) => (code === NO_COUNTRY ? 'No country' : countryName(code));
